import type { FieldValues, UseFormWatch } from 'react-hook-form';

import { useCallback, useMemo } from 'react';

import type { ConditionalRule, FieldCondition, FieldConfig, FieldGroup } from './types';

export const useConditionalFields = <T extends FieldValues>(
  fields: FieldConfig<T>[] = [],
  fieldGroups: FieldGroup<T>[] = [],
  watch: UseFormWatch<T>,
) => {
  const formData = watch();

  const evaluateCondition = useCallback(
    (condition: FieldCondition<T>): boolean => {
      const fieldValue = formData[condition.field];
      const { operator, value } = condition;

      switch (operator) {
        case 'equals':
          return fieldValue === value;
        case 'not_equals':
          return fieldValue !== value;
        case 'includes':
          return Array.isArray(fieldValue) ? fieldValue.includes(value) : String(fieldValue).includes(String(value));
        case 'not_includes':
          return Array.isArray(fieldValue) ? !fieldValue.includes(value) : !String(fieldValue).includes(String(value));
        case 'greater_than':
          return Number(fieldValue) > Number(value);
        case 'less_than':
          return Number(fieldValue) < Number(value);
        case 'truthy':
          return !!fieldValue;
        case 'falsy':
          return !fieldValue;
        default:
          return true;
      }
    },
    [formData],
  );

  const evaluateRule = useCallback(
    (rule: ConditionalRule<T>): boolean => {
      const conditions = Array.isArray(rule.when) ? rule.when : [rule.when];
      const logic = rule.logic || 'and';

      if (logic === 'or') {
        return conditions.some(evaluateCondition);
      }

      return conditions.every(evaluateCondition);
    },
    [evaluateCondition],
  );

  const visibleFields = useMemo(() => {
    return fields.filter((field) => {
      if (!field.conditional) return true;

      return evaluateRule(field.conditional);
    });
  }, [fields, evaluateRule]);

  const visibleFieldGroups = useMemo(() => {
    return fieldGroups.flatMap((group) => {
      const isGroupVisible = group.conditional ? evaluateRule(group.conditional) : true;

      if (!isGroupVisible) return [];

      const visibleGroupFields = group.fields.filter((field) => {
        if (!field.conditional) return true;

        return evaluateRule(field.conditional);
      });

      return [{ ...group, fields: visibleGroupFields }];
    });
  }, [fieldGroups, evaluateRule]);

  const allVisibleFields = useMemo(() => {
    const flatFields = visibleFields;
    const groupFields = visibleFieldGroups.flatMap((group) => group.fields);

    return [...flatFields, ...groupFields];
  }, [visibleFields, visibleFieldGroups]);

  const dependencyFields = useMemo(() => {
    const deps = new Set<string>();

    const extractDependencies = (rule: ConditionalRule<T>) => {
      const conditions = Array.isArray(rule.when) ? rule.when : [rule.when];
      conditions.forEach((condition) => {
        deps.add(condition.field);
      });
    };

    fields.forEach((field) => {
      if (field.conditional) {
        extractDependencies(field.conditional);
      }
    });

    fieldGroups.forEach((group) => {
      if (group.conditional) {
        extractDependencies(group.conditional);
      }

      group.fields.forEach((field) => {
        if (field.conditional) {
          extractDependencies(field.conditional);
        }
      });
    });

    return Array.from(deps);
  }, [fields, fieldGroups]);

  return {
    visibleFields,
    visibleFieldGroups,
    allVisibleFields,
    dependencyFields,
    evaluateRule,
    evaluateCondition,
  };
};
